import { YuLifeLocale } from "hooks";
import { StaticRoutes } from "@routes";
import { Anchor } from "components/atoms/Anchor";
import { ReactElement } from "react";
import { Accordion, AccordionItem } from "components/molecules/Accordion";
import { P1, P2 } from "components/atoms/Paragraph";

type Data = Record<
  "uk" | "us" | "za",
  {
    faq?: ReactElement;
  }
>;

const data: Data = {
  uk: {
    faq: (
      <Accordion>
        <AccordionItem title="What are examples of employee benefits?">
          <P2 pt="0">
            Employee benefits come in many forms. Some of the most common are group life insurance,
            group health insurance, group critical illness insurance and group income protection.
            Other examples include extra holiday entitlement, pension contributions, student loan
            assistance and even flexible working arrangements.
          </P2>
        </AccordionItem>
        <AccordionItem title="How much do employee benefits cost in the UK?">
          <P2 pt="0">
            The cost of employee benefits depends on the type of company benefits package you want
            to offer. It also can depend on the make-up of your workforce.
          </P2>
        </AccordionItem>
        <AccordionItem title="What is the most sought-after employee benefits insurance cover?">
          <P2 pt="0">
            Health generally tops the list of employees' life priorities when surveyed. As a result,
            group life insurance, critical illness cover, group income protection and employee
            wellbeing perks are among the most desired worker benefits.
          </P2>
        </AccordionItem>
        <AccordionItem title="Which businesses are eligible for employee benefits insurance in the UK?">
          <P2 pt="0">
            Any UK-registered company or partnership is eligible to set up an employee benefits
            scheme for its staff. The minimum for YuLife schemes is at least three members of your
            team.
          </P2>
        </AccordionItem>
        <AccordionItem title="What is the importance of company benefits on employee retention?">
          <P2 pt="0">
            The employee benefits packages you offer can have a significant impact on the success of
            your business. In an employees market, the culture of a business is central to
            productivity, attraction and retention – and employee benefits are one meaningful way to
            create a caring culture. Providing a comprehensive employee benefits scheme is proven to
            boost productivity, increase staff loyalty and help attract the best talent. Read the
            independent study of YuLife ROI{" "}
            <Anchor href="https://resource.yulife.com/forrester-total-economic-impact-of-yulife">
              here.
            </Anchor>
          </P2>
        </AccordionItem>
        <AccordionItem title="How do you present a company benefits package to employees?">
          <P2 pt="0">
            Taking your worker benefits strategy to the next level and to drive engagement requires
            seamless integration to limit the number of touch points employees need to go through to
            access these essential services.
          </P2>
          <P2>
            The YuLife employee app provides an opportunity to combine all the different elements of
            a benefits package into a single platform. This can drive increased uptake and
            engagement, ultimately better serving your team when they need it most.
          </P2>
          <P2>
            <Anchor href={StaticRoutes.featuresWellbeingHub}>The Wellbeing Hub</Anchor> provides a
            gateway to best-in-class employee engagement tools including 24/7 virtual GP, EAP,
            online gym classes and popular mindfulness apps. Our carefully selected partners across
            travel, fitness, retail and more provide a diverse range of employee rewards that offer
            something for everyone.
          </P2>
        </AccordionItem>
      </Accordion>
    ),
  },
  us: {
    faq: (
      <Accordion>
        <AccordionItem title="What are examples of employee benefits?">
          <P2 pt="0">
            Employee benefits come in many forms. Some of the most common are group life insurance,
            health insurance, group critical illness insurance and group income protection. Other
            examples include extra holiday entitlement, 401k contributions, student loan assistance
            and even flexible working arrangements.
          </P2>
        </AccordionItem>
        <AccordionItem title="How much do employee benefits cost in the US?">
          <P2 pt="0">
            The cost of employee benefits depends on the type of company benefits package you want
            to offer. It also can depend on the make-up of your workforce.
          </P2>
        </AccordionItem>
        <AccordionItem title="What is the most sought-after employee benefits insurance cover?">
          <P2 pt="0">
            Physical, mental and financial wellbeing generally top the list of employees' life
            priorities when surveyed. As a result, company-sponsored retirement plans, group health
            and risk insurance, mental health support and wellbeing perks are among the most desired
            worker benefits.
          </P2>
        </AccordionItem>
        <AccordionItem title="Which businesses are eligible for employee benefits insurance in the US?">
          <P2 pt="0">
            Any US-registered company or partnership is eligible to set up employee benefits for its
            staff.
          </P2>
        </AccordionItem>
        <AccordionItem title="What is the importance of company benefits on employee retention?">
          <P2 pt="0">
            The employee benefits packages you offer can have a significant impact on the success of
            your business.
          </P2>
          <P2>
            In an employees market, the culture of a business is central to productivity, attraction
            and retention. And employee benefits are one meaningful way to create a caring culture.
          </P2>
          <P2>
            Providing a comprehensive employee benefits offering is proven to boost productivity,
            increase staff loyalty and help attract the best talent.
          </P2>
          <P2>
            Read the independent study of YuLife ROI{" "}
            <Anchor href="https://resource.yulife.com/forrester-total-economic-impact-of-yulife">
              here.
            </Anchor>
          </P2>
        </AccordionItem>
        <AccordionItem title="How do you present a company benefits package to employees?">
          <P2 pt="0">
            Taking your worker benefits strategy to the next level and to drive engagement requires
            seamless integration to limit the number of touchpoints employees need to go through to
            access these essential services.
          </P2>
          <P2>
            The YuLife employee app provides an opportunity to combine all the different elements of
            a benefits package into a single platform. This can drive increased uptake and
            engagement, ultimately better serving your team when they need it most.
          </P2>
          <P2>
            The Wellbeing Hub provides a gateway to best-in-class employee engagement tools
            including EAP, online gym classes and popular mindfulness apps. Our carefully selected
            partners across travel, fitness, retail and more provide a diverse range of
            <Anchor href={StaticRoutes.featuresRewards}> employee rewards</Anchor> that offer
            something for everyone.
          </P2>
        </AccordionItem>
      </Accordion>
    ),
  },
  za: {
    faq: (
      <Accordion>
        <AccordionItem title="What are examples of employee benefits?">
          <P2 pt="0">
            Employee benefits come in many forms. The most common benefits include Lifeover, Funeral
            Cover, Lump Sum Disability and Income Protection. Others extend to perks like additional
            leave, contributions to pensions, student loan support and the option for flexible work
            schedules.
          </P2>
        </AccordionItem>
        <AccordionItem title="How much do employee benefits cost in South Africa?">
          <P2 pt="0">
            The cost of employee benefits depends on the type of employee benefits package you want
            to provide. It also can depend on the make-up of your staff.
          </P2>
        </AccordionItem>
        <AccordionItem title="What are the most sought-after employee benefits?">
          <P2 pt="0">
            When polled about their priorities, health typically ranks as the top concern for most
            employees. Consequently, Group Life Cover, Income Protection and employee wellbeing
            advantages rank high on the list of the most sought-after workplace benefits.
          </P2>
        </AccordionItem>
        <AccordionItem title="Which businesses are eligible for employee benefits insurance in South Africa?">
          <P2 pt="0">
            Any South Africa-registered company or partnership is eligible to set up an employee
            benefits scheme for its staff. The minimum for YuLife schemes is at least five members.
          </P2>
        </AccordionItem>
        <AccordionItem title="What is the importance of company benefits on employee retention?">
          <P2 pt="0">
            The employee benefits package you provide to your team can profoundly influence your
            business's prosperity. In a market where employees hold significant sway, a company's
            culture becomes vital for productivity, talent attraction and, most importantly,
            retention. Employee benefits are a powerful tool to foster a caring culture. An
            all-inclusive benefits program has been shown to boost productivity, bolster employee
            loyalty and engagement, and aid in recruiting top-tier talent. Read the independent
            study of YuLife ROI{" "}
            <Anchor href="https://resource.yulife.com/forrester-total-economic-impact-of-yulife">
              here.
            </Anchor>
          </P2>
        </AccordionItem>
        <AccordionItem title="How do you present a company benefits package to employees?">
          <P2 pt="0">
            Elevating your employee benefits strategy and fostering engagement requires a
            streamlined integration that minimises the steps your team members need to go through in
            order to access these crucial services.
          </P2>
          <P2>
            The YuLife app consolidates all components of a benefits package into a singular
            platform, driving higher usage and involvement, and effectively supporting your team
            when they need it the most.
          </P2>
          <P2>
            Our Wellbeing Hub also serves as an access point to best-in-class employee engagement
            tools such as Employee Assistance Programmes, online fitness classes and widely-used
            mindfulness apps. Our meticulously chosen rewards partners in the spheres of retail,
            fashion, utilities, devices and beyond provide a diverse array of rewards catering to
            every employee's unique preferences.
          </P2>
        </AccordionItem>
      </Accordion>
    ),
  },
};

export default data;
